<template>
  <long-query-layout
    :resource-name="resourceName"
    :query-filter="queryFilter"
    :map-query-filter-for-api="mapQueryFilterForApi"
    :apply-query-filter="applyQueryFilter"
    :result-fetch-url.sync="resultTable.url"
    :columns.sync="resultTable.columns"
    :group-headers.sync="resultTable.groupHeaders"
    :active-tab.sync="activeTab"
    :excel-properties-handler="excelPropertiesHandler.bind(this)"
    :convert-menu-default-query-filter="convertMenuDefaultQueryFilter"
    @result-area-size-change="updateTableSize"
    @query-completed="loadData"
    @query-id-change="queryId = $event"
  >
    <!-- -------------------------------------------------------- -->
    <!-- ヘッダのアクション -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:header-actions="slotProps">
      <gl-switch
        :value="isDetail"
        :disabled="!(slotProps.queryId && activeTab == 'result')"
        @input="toggleDetail"
      >
        詳細
      </gl-switch>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析条件 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:filter>
      <!-- 期間 -->
      <query-filter-element id="term" title="期間" contentWidth="medium">
        <div><gl-sub-text>当期間</gl-sub-text></div>
        <filter-term
          v-bind.sync="queryFilter.term.base"
          :default-week-count="8"
        />
        <div><gl-sub-text>前期間</gl-sub-text></div>
        <filter-term
          v-bind.sync="queryFilter.term.compare"
          :default-week-count="8"
        />
      </query-filter-element>

      <!-- 商品分類 -->
      <query-filter-element id="item" title="商品分類">
        <gl-select-company :company.sync="queryFilter.item.company" />
        <div>
          <md-radio v-model="queryFilter.item.selectMode" value="category">
            分類
            <gl-sub-text>で指定</gl-sub-text>
          </md-radio>
          <md-radio v-model="queryFilter.item.selectMode" value="item">
            商品
            <gl-sub-text>で指定</gl-sub-text>
          </md-radio>
        </div>
        <filter-item-category
          v-if="queryFilter.item.selectMode === 'category'"
          v-bind.sync="queryFilter.item"
          :pos-restriction="true"
        />
        <filter-item
          v-if="queryFilter.item.selectMode === 'item'"
          v-bind.sync="queryFilter.item"
          :pos-restriction="true"
        />
      </query-filter-element>

      <!-- 店舗 -->
      <query-filter-element id="store" title="店舗">
        <filter-store v-bind.sync="queryFilter.store" />
      </query-filter-element>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析結果 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:result>
      <md-tabs
        :md-active-tab="activeResultTab"
        @md-changed="activeResultTab = $event"
      >
        <md-tab id="summary" md-label="ABC分析">
          <gl-table v-bind="resultTable.summary" />
        </md-tab>
        <md-tab
          id="item"
          md-label="商品詳細"
          :md-disabled="!existItemResultUrl"
        >
          <gl-table v-bind="resultTable.item" ref="myTable" />
          <md-dialog
            :md-active.sync="creatingItemGroup"
            :md-close-on-esc="false"
            :md-click-outside-to-close="false"
          >
            <md-dialog-content>
              <div style="display:flex;align-items:center">
                <spinner :diameter="30" />
                <span style="margin-left:var(--spacing-medium)">
                  商品グループを作成しています…
                </span>
              </div>
            </md-dialog-content>
          </md-dialog>
        </md-tab>
      </md-tabs>
      <pos-transition-dialog
        :open.sync="transitionOpen"
        :trigger="transitionTriggerEvent"
        :title="transitionData.title"
        :destinations="destinations"
        @click="transition"
      ></pos-transition-dialog>
    </template>
  </long-query-layout>
</template>

<script>
// 分析条件, 分析結果のためのレイアウト
import LongQueryLayout from '@/components/layout/LongQueryLayout.vue'
// 分析条件
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterTerm from '@/components/FilterTerm.vue'
import FilterItemCategory from '@/components/FilterItemCategory.vue'
import FilterItem from '@/components/FilterItem2.vue'
import FilterStore from '@/components/FilterStore.vue'
// 分析条件モデル
import term from '@/queryfilter/term'
import item from '@/queryfilter/item2'
import store from '@/queryfilter/store'
// 分析結果
import GlTable from '@/components/GlTable.vue'
import PosTransitionDialog from '@/components/PosTransitionDialog.vue'
import Spinner from '@/components/Spinner.vue'

import repository from '@/repositories/Repository'
import {
  standardTableCustomFunction,
  standardWithSearchCustomFunction,
} from '@/util/custom_formatters'

const detailOptionParser = (rowData, value, colName) => {
  return {
    abcAmount: rowData.summary_abc_amount,
    abcQty: rowData.summary_abc_qty,
    rankMoveAmount: rowData.summary_abc_amount
      ? colName.split('_').pop()
      : null,
    rankMoveQty: rowData.summary_abc_qty ? colName.split('_').pop() : null,
    isNew: colName.includes('new'),
    isInhouse: colName.includes('inhouse'),
  }
}

let sizeFixed = false

export default {
  components: {
    QueryFilterElement,
    FilterTerm,
    FilterItem,
    FilterItemCategory,
    FilterStore,
    LongQueryLayout,
    GlTable,
    PosTransitionDialog,
    Spinner,
  },
  data() {
    return {
      resourceName: 'shrimp',
      destinations: [
        {
          resourceName: 'chocostick-i',
          label: '商品xKPI',
        },
        {
          resourceName: 'chocostick-s',
          label: '店舗xKPI',
        },
        {
          resourceName: 'chocostick-t',
          label: '期間xKPI (日別)',
        },
        {
          resourceName: 'chocostick-m',
          label: 'メーカーxKPI',
        },
      ],
      /**
       * 表示タブ（分析条件、分析結果）
       *
       * 表示タブをプログラム側から切り替えたい場合をこれを使います。
       */
      activeTab: undefined,
      /**
       * 分析結果の表示タブ
       */
      activeResultTab: 'summary',
      /**
       * 分析条件
       */
      queryFilter: {
        term: {
          order: 1,
          base: term.get(),
          compare: term.get(),
          summaryKey: 'termCompare',
        },
        item: { order: 2, summaryKey: 'itemGroup', ...item.get() },
        store: { order: 3, ...store.get() },
      },
      /**
       * 分析結果
       */
      resultTable: {
        summary: {
          columns: undefined,
          groupHeaders: undefined,
          sortColumnName: 'summary_abc_amount', // Dummy
          sortOrder: 'asc',
          url: undefined,
          width: 0,
          height: 0,
          customFormatter: {
            linkify: (rowData, cell, value, colName) => {
              if (!value || parseInt(value, 10) === 0) {
                cell.textContent = ''
                return
              }

              cell.classList.add('link-cell')
              cell.addEventListener('click', e => {
                const options = detailOptionParser(rowData, value, colName)

                const title = []
                title.push(options.abcAmount ? '金額:' + options.abcAmount : '')
                title.push(options.abcQty ? '点数:' + options.abcQty : '')
                title.push(options.isNew ? '新商品' : '')
                title.push(options.isInhouse ? '自社商品' : '')

                this.transitionTriggerEvent = e
                this.transitionData.title = title.join(' ')
                this.transitionData.abcAmount = options.abcAmount
                this.transitionData.abcQty = options.abcQty
                this.transitionData.isNew = options.isNew
                this.transitionData.isInhouse = options.isInhouse

                this.transitionOpen = true
              })
            },
            linkifyDetail: (rowData, cell, value, colName) => {
              if (value === null) {
                cell.style.backgroundColor = '#ddd'
              }

              if (!value || parseInt(value, 10) === 0) {
                cell.textContent = ''
                return
              }

              cell.classList.add('link-cell')
              // こちらのリンクはワークテーブルの有無関係なくクリック可能なためリンク色を固定
              cell.style.setProperty('color', '#448aff', 'important')
              cell.addEventListener('click', () => {
                this.showItemData(detailOptionParser(rowData, value, colName))
              })
            },
          },
        },
        item: {
          columns: undefined,
          groupHeaders: undefined,
          sortColumnName: 'item_code',
          sortOrder: 'asc',
          url: undefined,
          width: 0,
          height: 0,
          customFormatter: {
            standard: standardTableCustomFunction,
            linkifySearch: standardWithSearchCustomFunction,
          },
        },
      },
      /**
       * 期間展開の有無
       */
      isDetail: false,
      /**
       * 画面遷移のための一時商品グループ作成中
       */
      creatingItemGroup: false,
      /**
       * この画面が持つ分析条件を、クエリのペイロードに変換する
       */
      mapQueryFilterForApi: queryFilter => ({
        term: {
          base: term.mapToApiPayload(queryFilter.term.base),
          compare: term.mapToApiPayload(queryFilter.term.compare),
        },
        item: item.mapToApiPayload(queryFilter.item),
        store: store.mapToApiPayload(queryFilter.store),
      }),
      /**
       * 渡された分析条件をこの画面の分析条件に反映する
       */
      applyQueryFilter: queryFilter => {
        term.applyPayload(this.queryFilter.term.base, queryFilter.term.base)
        term.applyPayload(
          this.queryFilter.term.compare,
          queryFilter.term.compare,
        )
        item.applyPayload(this.queryFilter.item, queryFilter.item)
        store.applyPayload(this.queryFilter.store, queryFilter.store)
      },
      /**
       * Excel出力のパラメータ生成
       */
      excelPropertiesHandler() {
        const { name, order } = this.$refs.myTable.getCurrentSort()
        return {
          sortColumnName: name,
          sortOrder: order,
        }
      },
      transitionOpen: false,
      transitionTriggerEvent: undefined,
      transitionData: {
        title: undefined,
        abcAmount: undefined,
        abcQty: undefined,
        isNew: false,
        isInhouse: false,
      },
    }
  },
  computed: {
    // 商品詳細タブのデータの存在確認のため。
    existItemResultUrl() {
      return Boolean(this.resultTable.item.url)
    },
  },
  methods: {
    async showItemData({
      abcAmount,
      abcQty,
      rankMoveAmount,
      rankMoveQty,
      isInhouse,
    }) {
      const options = []
      if (abcAmount) {
        options.push(`abcAmount=${abcAmount}`)
      }
      if (abcQty) {
        options.push(`abcQty=${abcQty}`)
      }
      if (rankMoveAmount) {
        options.push(`rankMoveAmount=${rankMoveAmount}`)
      }
      if (rankMoveQty) {
        options.push(`rankMoveQty=${rankMoveQty}`)
      }
      // isNew (新商品) は商品詳細遷移時には指定されません。
      if (isInhouse) {
        options.push('isInhouse=true')
      }

      const url = await this.$longQuery.getQueryResultFetchUrl(
        this.resourceName,
        this.queryId,
        `item?${options.join('&')}`,
      )

      this.resultTable.item.url = url
      this.activeResultTab = 'item'
    },
    toggleDetail(value) {
      this.isDetail = value

      const alwaysShowColumns = [
        'item_code',
        'item_name',
        'abc_amount_compare',
        'abc_amount',
        'composition_ratio_amount_diff',
        'abc_qty_compare',
        'abc_qty',
        'composition_ratio_qty_diff',
      ]

      this.resultTable.item.columns.forEach(c => {
        if (!alwaysShowColumns.includes(c.colName)) {
          c.hidden = !value
        }
      })

      this.$refs.myTable.init()
    },
    /**
     * エクセルデータダウンロード
     */
    async downloadExcel() {
      const { name, order } = this.$refs.myTable.getCurrentSort()
      const url = await this.$longQuery.getExcelUrl(
        'shrimp',
        this.queryId,
        name,
        order,
      )
      window.open(url)
    },
    onQueryCompleted() {
      this.toggleDetail(this.isDetail)
    },
    async loadData(queryInfo) {
      const colModels = await Promise.all([
        repository.giggle
          .get(
            this.$longQuery.getColModelFetchUrl(
              this.resourceName,
              queryInfo.id,
              'summary',
            ),
          )
          .then(r => r.data),
        repository.giggle
          .get(
            this.$longQuery.getColModelFetchUrl(
              this.resourceName,
              queryInfo.id,
              'item',
            ),
          )
          .then(r => r.data),
      ])

      const summaryCol = colModels[0]
      const itemCol = colModels[1]

      this.resultTable.summary.groupHeaders = summaryCol.groupHeader
      this.resultTable.summary.columns = summaryCol.colModel
      this.resultTable.item.groupHeaders = itemCol.groupHeader
      this.resultTable.item.columns = itemCol.colModel

      // 結果表示テーブルのURL設定
      const summaryUrl = await this.$longQuery.getQueryResultFetchUrl(
        this.resourceName,
        queryInfo.id,
        'summary',
      )
      this.resultTable.summary.url = summaryUrl

      // 分析をしたのであれば、商品詳細の中身をクリアする
      this.resultTable.item.url = undefined
      this.activeResultTab = 'summary'

      this.toggleDetail(this.isDetail)
    },
    async transition(destination) {
      this.transitionOpen = false

      const override = {
        immediate: true,
        term: {
          termType: this.queryFilter.term.base.termType,
          dateFrom: this.queryFilter.term.base.dateFrom,
          dateTo: this.queryFilter.term.base.dateTo,
        },
        option: {
          evaluations: [
            'amount',
            'qty',
            'amount_per_qty',
            'qty_pi',
            'amount_pi',
          ],
        },
      }

      try {
        // 商品グループ作成と条件設定
        const options = []
        if (this.transitionData.abcAmount) {
          options.push(`abcAmount=${this.transitionData.abcAmount}`)
        }
        if (this.transitionData.abcQty) {
          options.push(`abcQty=${this.transitionData.abcQty}`)
        }
        if (this.transitionData.isNew) {
          options.push('isNew=true')
        }
        if (this.transitionData.isInhouse) {
          options.push('isInhouse=true')
        }

        this.creatingItemGroup = true
        const itemGroupUrl = await this.$longQuery.getQueryResultFetchUrl(
          this.resourceName,
          this.queryId,
          `items?${options.join('&')}`,
        )
        const itemGroup = (await repository.giggle.get(itemGroupUrl)).data

        override['item'] = {
          selectMode: 'item',
          company: this.queryFilter.item.company,
          items: [],
          categories: null,
          item: null,
          category: null,
          itemGroupId: itemGroup.itemGroupId,
        }
      } finally {
        this.creatingItemGroup = false
      }

      const url = `${process.env.BASE_URL}query-${destination}?reference=${
        this.queryId
      }&override=${JSON.stringify(override)}`

      window.open(url)
    },
    /**
     * 結果表示テーブルのサイズを更新する
     */
    updateTableSize(size) {
      if (sizeFixed) {
        return
      }

      const mergin = 16

      const w = size.width
      const h = size.height - mergin * 3 // TODO サイズ調整がかなり無理矢理…

      this.resultTable.summary.width = w
      this.resultTable.summary.height = h
      this.resultTable.item.width = w
      this.resultTable.item.height = h

      sizeFixed = true
    },
    // デフォルトQueryFilterの変換処理
    // 商品グループは一時的なものなのでメニューのデフォルト条件
    // としては保持しない。
    convertMenuDefaultQueryFilter(queryFilter) {
      queryFilter.item.itemGroupId = undefined
      return queryFilter
    },
  },
  mounted() {
    document.querySelector('html').style.overflowX = 'hidden'
  },
}
</script>

<style lang="scss" scoped>
.md-tabs {
  // 結果エリア内のタブに分析結果が入る場合、結果エリアからの
  // マージンがspacing-largeあり、タブ内がspacing-mediumある。
  // 少々マージンを取りすぎなので、グリッドまでが通常のspacing-large
  // になるように調整する。
  // node-sassのバージョン（？）のせいなのかコンパイルされたあとのCSSが
  // "calc(var(--spacing-large) - var(--spacing-medium))"となってしまっていたので
  // ひとまず固定値にした。
  // margin: calc(0px - (var(--spacing-large) - var(--spacing-medium)));
  margin: -16px;
}
</style>
